import {IMatchPairsReport, QuestionAnswerEnum, QuestionReport, QuestionReportAnswer} from '../../../../../incase-domain/analytics';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';
import {IQuestionElements} from '../../../../../incase-domain/elements/question';
import {IElementQuestionMatchPair} from '../../../../../incase-domain/elements/question/questionMatchPair';
import {IElementQuestionAnswer} from '../../../../../incase-domain/elements/base/questionElementBase';
import {IShape} from '../../../../../incase-domain/elements/question/imageMarkup';
import {IElementSequenceQuestion} from '../../../../../incase-domain/elements/question/questionSequence';
import {IElementLikertScale} from '../../../../../incase-domain/elements/question/likertScale';
import {ISlide} from '../../../../../incase-domain/slide';

const getPercentage = (noOfAnswers: number, ordinal: number): number =>
    Math.ceil(((ordinal + 1) * 100) / ((noOfAnswers * (noOfAnswers + 1)) / 2));

const getMatchPairAnswer = (question: IElementQuestionMatchPair, index: number): string => {
    const left = question.leftElement.find((el) => el.id === question.pairs[index].leftElementId);
    const right = question.rightElement.find((el) => el.id === question.pairs[index].rightElementId);
    return `${left?.text} - ${right?.text}`;
};

const areAllAnswersDebatable = (question: IQuestionElements): boolean =>
    'answers' in question &&
    Array.isArray(question.answers) &&
    question.answers.length > 0 &&
    question.answers.every((answer) => answer.type === QuestionAnswerEnum.DEBATABLE);

const addAllCorrectAnswerIfNeeded = (answers: QuestionReportAnswer[], question: IQuestionElements): QuestionReportAnswer[] => {
    const allCorrectAnswer = {
        answerText: 'All correct',
        noOfAnswers: 0,
        type: QuestionAnswerEnum.CORRECT as const,
        percentage: 0,
    };
    const needsAllCorrectAnswer =
        !question.isDebatable &&
        !areAllAnswersDebatable(question) &&
        ((question.type !== ELEMENT.YES_NO_QUESTION &&
            [ELEMENT.QUESTION, ELEMENT.MULTI_SELECT, ELEMENT.PHOTO_ANSWER_QUESTION].includes(question.type) &&
            !question.singleCorrect) ||
            [ELEMENT.MATCH_PAIR_QUESTION, ELEMENT.SEQUENCE_QUESTION].includes(question.type) ||
            (ELEMENT.IMAGE_MARKUP === question.type && (question.markedAreas?.length ?? 0) > 1));
    return needsAllCorrectAnswer ? [allCorrectAnswer, ...answers] : answers;
};

const getAnswers = (question: IQuestionElements): QuestionReportAnswer[] => {
    let reportAnswers: QuestionReportAnswer[];
    switch (question.type) {
        case ELEMENT.YES_NO_QUESTION:
            reportAnswers = [
                {
                    answerText: 'No',
                    noOfAnswers: 140,
                    type: question.correct === 0 ? QuestionAnswerEnum.CORRECT : QuestionAnswerEnum.INCORRECT,
                    percentage: 70,
                },
                {
                    answerText: 'Yes',
                    noOfAnswers: 60,
                    type: question.correct === 1 ? QuestionAnswerEnum.CORRECT : QuestionAnswerEnum.INCORRECT,
                    percentage: 30,
                },
            ];
            break;
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            reportAnswers = question.answers?.map((a: IElementQuestionAnswer, i: number, answers: IElementQuestionAnswer[]) => ({
                answerText: a.text || String(i),
                noOfAnswers: (i + 1) * 100,
                type: a.type,
                percentage: getPercentage(answers.length, i),
            }));
            break;
        case ELEMENT.SEQUENCE_QUESTION:
            reportAnswers = [
                ...question.sequence.map((a: IElementQuestionAnswer, i: number, answers: IElementQuestionAnswer[]) => ({
                    answerText: a.text,
                    noOfAnswers: (i + 1) * 100,
                    type: undefined,
                    percentage: getPercentage(answers.length, i),
                })),
            ];
            break;
        case ELEMENT.IMAGE_MARKUP:
            reportAnswers = [
                ...(question.markedAreas || []).map((_: IShape, i: number, areas) => ({
                    answerText: `${i + 1}`,
                    noOfAnswers: (i + 1) * 100,
                    type: QuestionAnswerEnum.CORRECT as const,
                    percentage: getPercentage(areas.length, i),
                })),
            ];
            break;
        case ELEMENT.MATCH_PAIR_QUESTION:
            reportAnswers = [
                ...question.pairs.map((_: {leftElementId: string; rightElementId: string}, i: number, pairs) => ({
                    answerText: getMatchPairAnswer(question, i),
                    noOfAnswers: (i + 1) * 100,
                    type: QuestionAnswerEnum.CORRECT as const,
                    percentage: getPercentage(pairs.length, i),
                })),
            ];
            break;
        default:
            reportAnswers =
                'answers' in question
                    ? question.answers?.map((a: IElementQuestionAnswer, i: number, answers: IElementQuestionAnswer[]) => ({
                          answerText: a.text || String(i),
                          noOfAnswers: (i + 1) * 100,
                          type: a.type,
                          percentage: getPercentage(answers.length, i),
                      })) || []
                    : [];
    }
    return addAllCorrectAnswerIfNeeded(reportAnswers, question);
};

const STATIC_MATCH_PERCENTAGES = [
    78, 23, 45, 67, 89, 34, 56, 12, 91, 65, 43, 87, 29, 76, 92, 18, 54, 71, 36, 47, 83, 62, 25, 94, 59, 72, 31, 86, 48,
];
const STATIC_TOTAL_VALUES = [456, 723, 892, 345, 678, 534, 289, 567, 834, 412, 756, 623, 789, 134, 567, 890, 234, 445, 667, 888];

function getStaticPercentage(index: number): number {
    return STATIC_MATCH_PERCENTAGES[index % STATIC_MATCH_PERCENTAGES.length];
}

function getStaticTotal(index: number): number {
    return STATIC_TOTAL_VALUES[index % STATIC_TOTAL_VALUES.length];
}

function createLabelMatrix(
    leftLabels: {id: string; text: string}[],
    rightLabels: {id: string; text: string}[],
    pairs: {leftElementId: string; rightElementId: string}[]
): Record<string, Record<string, {count: number; correct: boolean}>> {
    const matrix: Record<string, Record<string, {count: number; correct: boolean}>> = {};
    let percentageIndex = 0;

    leftLabels.forEach((leftLabel) => {
        matrix[leftLabel.text] = {};
        rightLabels.forEach((rightLabel) => {
            const percentage = getStaticPercentage(percentageIndex);
            const isCorrectPair = pairs.some((pair) => pair.leftElementId === leftLabel.id && pair.rightElementId === rightLabel.id);

            matrix[leftLabel.text][rightLabel.text] = {
                count: percentage,
                correct: isCorrectPair,
            };
            percentageIndex += 1;
        });
    });

    return matrix;
}

function createTotals(labels: string[]): Record<string, number> {
    const totals: Record<string, number> = {};
    labels.forEach((label, index) => {
        totals[label] = getStaticTotal(index);
    });
    return totals;
}

function getUniqueElements(elements: Array<{id: string; text: string}>): string[] {
    const uniqueMap = new Map<string, string>();
    elements.forEach((el) => {
        const key = `${el.id}-${el.text}`;
        if (!uniqueMap.has(key)) {
            uniqueMap.set(key, el.text);
        }
    });
    return Array.from(uniqueMap.values());
}

function buildMatchPairsReport(question: IElementQuestionMatchPair): IMatchPairsReport {
    const leftLabels = (question.leftElement || []).map((el) => ({id: el.id, text: el.text}));
    const rightLabels = (question.rightElement || []).map((el) => ({id: el.id, text: el.text}));

    const labelMatrix = createLabelMatrix(leftLabels, rightLabels, question.pairs || []);
    const totals = createTotals([...leftLabels.map((l) => l.text), ...rightLabels.map((r) => r.text)]);

    return {
        leftLabels: leftLabels.map((l) => l.text),
        rightLabels: getUniqueElements(rightLabels),
        labelMatrix,
        totals,
    };
}

const DUMMY_SEQUENCE_TOTAL_ANSWERS = 100;

const getOrdinalLabel = (position: number): string => {
    switch (position % 10) {
        case 1:
            return `${position}st`;
        case 2:
            return `${position}nd`;
        case 3:
            return `${position}rd`;
        default:
            return `${position}th`;
    }
};

const distributePercentages = (answerIndex: number, answerCount: number): Record<string, number> => {
    const weights = Array.from({length: answerCount}, (_, rankIndex) => answerCount - Math.abs(answerIndex - rankIndex));
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    let remainingPercentage = 100;

    return weights.reduce((percentages, weight, index) => {
        const rank = getOrdinalLabel(index + 1);
        const percentage = index === weights.length - 1 ? remainingPercentage : Math.round((weight * 100) / totalWeight);
        remainingPercentage -= percentage;

        return {
            ...percentages,
            [rank]: percentage,
        };
    }, {} as Record<string, number>);
};

function buildSequenceQuestionReport(question: IElementSequenceQuestion, slide: ISlide | undefined): QuestionReport | null {
    if (!question.sequence?.length) {
        return null;
    }

    const rankDistributionKeys = question.sequence.map((_, index) => getOrdinalLabel(index + 1));
    const rankDistributionData = question.sequence.map((answer, index) => {
        const rankingPercentages = distributePercentages(index, question.sequence.length);

        return {
            answerText: answer.text,
            rankingScore: (index + 1) * DUMMY_SEQUENCE_TOTAL_ANSWERS,
            averageRanking: index + 1,
            noOfAnswers: DUMMY_SEQUENCE_TOTAL_ANSWERS,
            ...rankingPercentages,
        };
    });

    return {
        elementId: question.id,
        question: question.question,
        answers: question.sequence.map((answer, index) => {
            const rankingPercentages = distributePercentages(index, question.sequence.length);

            return {
                answerText: answer.text,
                noOfAnswers: DUMMY_SEQUENCE_TOTAL_ANSWERS,
                percentage: Math.round(DUMMY_SEQUENCE_TOTAL_ANSWERS / question.sequence.length),
                type: QuestionAnswerEnum.DEBATABLE,
                rankingScore: rankDistributionData[index].rankingScore,
                averageRanking: rankDistributionData[index].averageRanking,
                rankingPercentages,
            };
        }),
        slide: {id: slide?._id || '', name: slide?.name || ''},
        totalAnswers: DUMMY_SEQUENCE_TOTAL_ANSWERS,
        type: question.type,
        isDebatable: true,
        sequenceQuestionData: {
            rankDistributionData,
            rankDistributionKeys,
        },
    };
}

function buildLikertScaleReport(question: IElementLikertScale): QuestionReport {
    const scale = question.scale || [];
    return {
        question: question.question,
        answers: scale.map((scaleItem) => ({
            answerText: scaleItem.text,
            percentage: null,
        })),
        likertScaleData: (question.questions || []).map((questionData, i1) => ({
            questionText: questionData.text,
            questionId: questionData.id,
            ...scale.reduce((acc, scaleItem, i2) => {
                acc[scaleItem.text] = Math.round((i1 + i2) * 100);
                return acc;
            }, {} as {[key: string]: number}),
        })) as unknown as {[key: string]: number}[],
    } as unknown as QuestionReport;
}

const DUMMY_YES_ANSWERS = 100;
const DUMMY_NO_ANSWERS = 300;
const DUMMY_TOTAL_ANSWERS = DUMMY_YES_ANSWERS + DUMMY_NO_ANSWERS;

export function buildPreviewQuestionReport(question: IQuestionElements | null, slide: ISlide | undefined): QuestionReport | null {
    if (!question) {
        return null;
    }

    switch (question.type) {
        case ELEMENT.MATCH_PAIR_QUESTION:
            return {
                elementId: question.id,
                question: question.question,
                answers: [],
                slide: {id: slide?._id || '', name: slide?.name || ''},
                totalAnswers: DUMMY_TOTAL_ANSWERS,
                type: question.type,
                matchPairsReport: buildMatchPairsReport(question as IElementQuestionMatchPair),
            };
        case ELEMENT.LIKERT_SCALE:
            return buildLikertScaleReport(question as IElementLikertScale);
        case ELEMENT.SEQUENCE_QUESTION:
            return buildSequenceQuestionReport(question as IElementSequenceQuestion, slide);
        default:
            return {
                elementId: question.id,
                question: question.question,
                answers: getAnswers(question),
                slide: {id: slide?._id || '', name: slide?.name || ''},
                totalAnswers: DUMMY_TOTAL_ANSWERS,
                type: question.type,
            };
    }
}
